import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Check, Loader2 } from "lucide-react";
import { Panel, PanelHeader, Label, MetaRow, Chip } from "./primitives";
import { sfx } from "../lib/sound";

const LIMITS = [
  { k: "Position Limit", v: "20%" },
  { k: "Max Trade", v: "$500" },
  { k: "Max Drawdown", v: "10%" },
  { k: "Slippage Limit", v: "1%" },
  { k: "Leverage", v: "OFF" },
];

const CHECKS = ["Portfolio Exposure", "Position Limit", "Drawdown", "Slippage"];

const ACTIONS = [
  { side: "BUY", asset: "NVDA", value: 450 },
  { side: "BUY", asset: "SPY", value: 800 },
  { side: "SELL", asset: "TSLA", value: 320 },
];

export const GuardianSim = ({ compact = false }) => {
  const [actionIdx, setActionIdx] = useState(0);
  const [phase, setPhase] = useState("idle");
  const [passed, setPassed] = useState(0);
  const action = ACTIONS[actionIdx];
  const scaled = action.value > 500;

  const run = () => {
    if (phase === "running") return;
    setPhase("running");
    setPassed(0);
    CHECKS.forEach((_, i) => {
      setTimeout(() => {
        setPassed(i + 1);
        sfx.pass();
      }, 500 * (i + 1));
    });
    setTimeout(() => {
      setPhase("done");
      sfx.confirm();
    }, 500 * CHECKS.length + 400);
  };

  const cycle = () => {
    setActionIdx((actionIdx + 1) % ACTIONS.length);
    setPhase("idle");
    setPassed(0);
    sfx.tick();
  };

  return (
    <Panel data-testid="guardian-sim" className={compact ? "" : "bg-ink-850"}>
      <PanelHeader right={<Chip tone="gold">Simulation</Chip>}>
        <span className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-pulse" /> Guardian — Decision Engine</span>
      </PanelHeader>

      <div className={`grid ${compact ? "" : "md:grid-cols-2"}`}>
        <div className="border-b border-white/[0.08] p-4 md:border-b-0 md:border-r">
          <Label className="mb-2">Risk Constraints</Label>
          {LIMITS.map((l) => <MetaRow key={l.k} k={l.k} v={l.v} />)}
        </div>

        <div className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <Label>Proposed Action</Label>
            <button onClick={cycle} data-testid="guardian-next-action"
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-fog-600 transition-colors hover:text-fog-100">
              Next →
            </button>
          </div>
          <div className="mb-4 flex items-center gap-3 border border-white/[0.08] bg-ink-900 px-3 py-2.5">
            <span className={`font-mono text-xs font-bold ${action.side === "BUY" ? "text-pulse-soft" : "text-brass-bright"}`}>{action.side}</span>
            <span className="font-mono text-xs text-fog-100">{action.asset}</span>
            <span className="ml-auto font-mono text-xs text-fog-400">${action.value}</span>
          </div>

          <div className="space-y-1.5">
            {CHECKS.map((c, i) => (
              <div key={c} className="flex items-center justify-between border-b border-white/[0.05] py-1.5">
                <span className="font-mono text-[11px] text-fog-400">{c}</span>
                {i < passed ? (
                  <motion.span initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-pulse-soft">
                    <Check className="h-3 w-3" /> Pass
                  </motion.span>
                ) : phase === "running" && i === passed ? (
                  <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-brass-bright">
                    <Loader2 className="h-3 w-3 animate-spin" /> Checking
                  </span>
                ) : (
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-fog-600">—</span>
                )}
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {phase === "done" && (
              <motion.div
                key={`${actionIdx}-done`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-4 border px-3 py-2.5 text-center font-mono text-[11px] uppercase tracking-[0.2em] ${
                  scaled ? "border-brass/40 bg-brass/10 text-brass-bright" : "border-pulse/40 bg-pulse/10 text-pulse-soft"
                }`}
                data-testid="guardian-final-status"
              >
                {scaled ? "Scale Down — Simulation" : "Approved — Simulation"}
              </motion.div>
            )}
          </AnimatePresence>

          <button
            onClick={run}
            disabled={phase === "running"}
            data-testid="guardian-run-check"
            className="mt-4 w-full border border-pulse/30 bg-pulse/10 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-pulse-soft transition-all hover:bg-pulse/20 disabled:opacity-50"
          >
            {phase === "running" ? "Validating…" : phase === "done" ? "Re-run Check" : "Run Risk Check"}
          </button>
        </div>
      </div>
    </Panel>
  );
};
